"use client"

import { useState, useEffect } from 'react'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { useToast } from '@/hooks/use-toast'
import { Edit, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/lib/stores/auth-store'
import { getRedmineBaseUrl } from '@/lib/tenant-config'

interface EditNewsSidebarProps {
  isOpen: boolean
  article: any
  onClose: () => void
  onSuccess: () => void
}

export function EditNewsSidebar({ isOpen, article, onClose, onSuccess }: EditNewsSidebarProps) {
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { session } = useAuthStore()
  const { toast } = useToast()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Reset form whenever article changes or sidebar opens
  useEffect(() => {
    if (isOpen && article) {
      setTitle(article.title || '')
      setSummary(article.summary || '')
      setDescription(article.description || '')
      setSubmitting(false)
    }
  }, [article, isOpen, article?.id])

  const getAuthHeaders = () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (session?.credentials?.basicAuth) headers['Authorization'] = `Basic ${session.credentials.basicAuth}`
    else if (session?.apiKey) headers['X-Redmine-API-Key'] = session.apiKey
    headers['X-Redmine-Base-URL'] = getRedmineBaseUrl()
    return headers
  }

  const handleSubmit = async (e?: any) => {
    e?.preventDefault()
    if (!article) return
    
    if (!title.trim()) {
      toast({
        variant: 'destructive',
        title: 'Title Required',
        description: 'Please enter a title for your news post',
      })
      return
    }

    if (!description.trim()) {
      toast({
        variant: 'destructive',
        title: 'Description Required',
        description: 'Please enter a description for your news post',
      })
      return
    }

    try {
      setSubmitting(true)
      const headers = getAuthHeaders()
      const res = await fetch(`/api/news/${article.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ news: { title, summary, description } })
      })
      
      if (!res.ok) {
        const errorData = await res.json()
        
        // Handle different error types
        if (res.status === 401) {
          toast({
            variant: 'destructive',
            title: 'Authentication Failed',
            description: 'You are not authenticated. Please log in and try again.',
          })
        } else if (res.status === 403) {
          toast({
            variant: 'destructive',
            title: 'Permission Denied',
            description: 'You do not have permission to edit this news post.',
          })
        } else if (res.status === 404) {
          toast({
            variant: 'destructive',
            title: 'News Not Found',
            description: 'The news post could not be found.',
          })
        } else if (res.status === 422) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: errorData.details || errorData.error || 'Please check your input and try again.',
          })
        } else {
          toast({
            variant: 'destructive',
            title: 'Failed to Update News',
            description: errorData.error || errorData.details || 'An unexpected error occurred. Please try again.',
          })
        }
        return
      }

      // Success
      toast({
        title: 'News Post Updated',
        description: 'Your changes have been saved successfully.',
      })

      // First trigger the refresh
      await onSuccess()
      // Then close the sidebar
      onClose()
    } catch (e) {
      console.error('News update error:', e)
      toast({
        variant: 'destructive',
        title: 'Error Updating News',
        description: e instanceof Error ? e.message : 'Failed to update article',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!mounted) return null

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title="Edit News Post"
      subtitle="Update article content and details"
      icon={<Edit className="h-5 w-5" />}
      width="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-semibold">Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold">Summary</label>
          <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold">Description</label>
          <MarkdownEditor value={description} onChange={setDescription} height={300} />
        </div>
      </form>
    </GlobalSidebar>
  )
}
